// Shared notification state used by the global NotificationCenter modal and the
// management lists (Customer Support / Orders / Prescriptions).
//
// Three independent sets, all keyed by the same `${type}-${_id}` id
// (e.g. "query-123", "order-456", "prescription-789"):
//
//  * SEEN      — controls the MODAL. Set when the admin crosses/dismisses or
//                clicks "View & Handle". A seen item never pops up again.
//  * HIGHLIGHT — controls the TABLE ROW HIGHLIGHT. Set ONLY when the admin
//                clicks "View & Handle" on that notification, so exactly the
//                one order / prescription / support ticket the admin chose to
//                handle is highlighted (light green) in its list — not every
//                pending row. Cleared once the admin opens that row.
//  * OPENED    — records that a row was opened in its table (also suppresses
//                its modal). Kept for that suppression; no longer drives the
//                highlight.
//
// All persist in localStorage (per browser/admin) so they survive reloads.
// Ids are Mongo ObjectIds and never reused, so entries can live indefinitely;
// growth is capped below.

const SEEN_KEY = "seen_notifications";
const OPENED_KEY = "opened_notifications";
const HIGHLIGHT_KEY = "highlight_notifications";
// fired in-tab whenever either set changes so open screens re-render live
const CHANGE_EVENT = "notifications-store-changed";
const MAX = 1000;

const load = (key) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(key)) || []);
  } catch {
    return new Set();
  }
};

const save = (key, set) => {
  try {
    // Set preserves insertion order — keep the most recent MAX ids.
    localStorage.setItem(key, JSON.stringify([...set].slice(-MAX)));
  } catch {
    /* ignore quota / serialization errors */
  }
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    /* ignore (non-browser env) */
  }
};

const add = (key, id) => {
  if (!id) return;
  const set = load(key);
  if (set.has(id)) return;
  set.add(id);
  save(key, set);
};

// ---- SEEN (modal) ----
export const loadSeen = () => load(SEEN_KEY);
export const saveSeen = (set) => save(SEEN_KEY, set);
export const isSeen = (id) => load(SEEN_KEY).has(id);
export const markSeen = (id) => add(SEEN_KEY, id);

// ---- OPENED (row was opened → suppress its modal) ----
export const isOpened = (id) => load(OPENED_KEY).has(id);
export const markOpened = (id) => add(OPENED_KEY, id);

// ---- HIGHLIGHT (table row highlight, set by "View & Handle") ----
// Marks the single row the admin chose to handle so ONLY that row lights up.
export const isHighlighted = (id) => load(HIGHLIGHT_KEY).has(id);
export const markHighlight = (id) => add(HIGHLIGHT_KEY, id);
export const clearHighlight = (id) => {
  if (!id) return;
  const set = load(HIGHLIGHT_KEY);
  if (!set.has(id)) return;
  set.delete(id);
  save(HIGHLIGHT_KEY, set); // dispatches change event → lists re-render
};

// Opening a row in its table clears its highlight AND suppresses the modal for
// it (the admin has clearly dealt with it).
export const markRowOpened = (id) => {
  markOpened(id);
  markSeen(id);
  clearHighlight(id);
};

// Subscribe to any change (same tab via custom event, other tabs via `storage`).
// Returns an unsubscribe function.
export const subscribeSeen = (callback) => {
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
};

// NotificationCenter queue-id builders (shared so lists match the modal).
export const supportSeenId = (id) => `query-${id}`;
export const orderSeenId = (id) => `order-${id}`;
export const prescriptionSeenId = (id) => `prescription-${id}`;
